import axios from "axios";
import config from "../../config";

const baseUrl = config.baseUrl;
const endpoint = "/api/promotorEncuestado";

// ─── Paginación por cursor (endpoint principal) ───────────────────────────────

/**
 * Descarga TODOS los registros en bloques usando el cursor para exportar a Excel.
 * @param {function} onProgress - callback(porcentaje, filasCargadas, total)
 * @param {number}   total      - total de registros (para calcular progreso)
 * @param {number}   blockSize  - registros por bloque (default 500)
 */
export const obtenerTodosEncuestadosEnBloques = async (
  onProgress,
  total = 0,
  blockSize = 500
) => {
  const todos = [];
  let lastId  = 0;
  let hasMore = true;

  while (hasMore) {
    const resultado = await obtenerEncuestadosPaginado(lastId, blockSize);
    todos.push(...resultado.data);

    hasMore = resultado.hasMore;
    lastId  = resultado.nextCursor ?? 0;

    const porcentaje = total > 0
      ? Math.min(Math.round((todos.length / total) * 100), 99)
      : null;

    onProgress?.(porcentaje, todos.length, total);
  }

  return todos;
};

export const obtenerEncuestadosPaginado = async (lastId = 0, pageSize = 30) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}`, {
      params: { lastId, pageSize },
    });
    return response.data; // { data, nextCursor, hasMore }
  } catch (error) {
    console.error("Error obteniendo encuestados paginados:", error);
    throw error;
  }
};

export const obtenerTotalEncuestados = async () => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/total`);
    return response.data.total;
  } catch (error) {
    console.error("Error obteniendo total:", error);
    throw error;
  }
};

// ─── Filtros ──────────────────────────────────────────────────────────────────

export const obtenerEncuestadosPorMunicipio = async (municipioId) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/municipio/${municipioId}`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo encuestados por municipio:", error);
    throw error;
  }
};

export const obtenerEncuestadosPorDepartamento = async (departamentoId) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/departamento/${departamentoId}`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo encuestados por departamento:", error);
    throw error;
  }
};

export const obtenerEncuestadoPorEncuesta = async (idEncuesta) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/encuesta/${idEncuesta}`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo encuestado por id_encuesta:", error);
    throw error;
  }
};

/**
 * Filtra registros por promotor (encuestador).
 * @param {number} promotorId - ID del promotor
 * @returns {{ success: boolean, data: Array }}
 */
export const obtenerEncuestadosPorPromotor = async (promotorId) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/promotor/${promotorId}`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo encuestados por promotor:", error);
    throw error;
  }
};

/**
 * Busca registros por número de documento del titular.
 * @param {string} documento - Número de documento (ej: "1012345678")
 * @returns {{ success: boolean, total: number, data: Array }}
 */
export const obtenerEncuestadosPorDocumento = async (documento) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/documento/${documento}`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo encuestados por documento:", error);
    throw error;
  }
};

/**
 * Filtra registros por deseo de participación.
 * @param {0|1} valor - 0 = no desea participar | 1 = sí desea participar
 * @returns {{ success: boolean, total: number, data: Array }}
 */
export const obtenerEncuestadosPorDeseaParticipar = async (valor) => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/desea-participar/${valor}`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo encuestados por desea_participar:", error);
    throw error;
  }
};

// ─── Catálogos ────────────────────────────────────────────────────────────────

export const obtenerMunicipios = async () => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/municipios`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo municipios:", error);
    throw error;
  }
};

export const obtenerDepartamentos = async () => {
  try {
    const response = await axios.get(`${baseUrl}${endpoint}/departamentos`);
    return response.data;
  } catch (error) {
    console.error("Error obteniendo departamentos:", error);
    throw error;
  }
};

/**
 * Obtiene la lista de encuestadores (promotores) disponibles para poblar selectores.
 * @returns {{ success: boolean, data: Array<{ promotor_id, promotor_nombres, promotor_apellidos }> }}
 */
export const obtenerEncuestadoresDisponibles = async (municipioId = null) => {
  try {
    const params = municipioId ? { municipioId } : {};
    const response = await axios.get(`${baseUrl}${endpoint}/encuestadores`, { params });
    return response.data;
  } catch (error) {
    console.error("Error obteniendo encuestadores:", error);
    throw error;
  }
};